import { Box, Chip, Stack, Typography } from '@mui/material';
import { contactRows } from '../../components/contactRows';
import { InfoPanel as SharedInfoPanel } from '../../components/InfoPanel';
import { PerformanceBar } from '../../components/PerformanceBar';
import { ActivityList } from '../../components/ActivityList';
import { DataTable } from '../../components/DataTable';
import { DonutChart } from '../../components/DonutChart';
import { EmptyState } from '../../components/EmptyState';
import { Panel } from '../../components/Panel';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { shortDate } from '../../lib/dates';
import { SERVICE_NAME } from '../../lib/services';
import AirplanemodeActiveOutlined from '@mui/icons-material/AirplanemodeActiveOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import { RATE_TONE, type Carrier, type CarrierRate } from './sample';
import { RISK_TONE, type CarrierStats, type RouteRow } from './stats';

export const InfoPanel = ({ c }: { c: Carrier }) => (
  <SharedInfoPanel
    title="Carrier Information"
    rows={[
      ...contactRows(c),
      { label: 'Mode', value: c.mode },
      { label: 'Services', value: c.services.map((s) => SERVICE_NAME[s]).join(', ') || '–' },
      { label: 'Coverage', value: c.coverage || '–' },
      { label: 'Notes', value: c.notes || '–' },
    ]}
  />
);

/** Reliability and rollover rate come from our shipments with this carrier; rate competitiveness from the rates it gave */
export function PerformancePanel({ c, st }: { c: Carrier; st: CarrierStats }) {
  return (
    <Panel
      title="Performance Overview"
      subtitle="Reliability: shipments that ran on schedule (not delayed, not rolled over). Rollover: a booking pushed to a later sailing or flight. Rate competitiveness: how close its rates are to the lowest on the same route."
    >
      <Stack spacing={1.5}>
        <PerformanceBar label="Reliability" kpi="carrierReliability" value={st.reliability} tone="success" />
        <PerformanceBar label="Rollover Rate" kpi="rolloverAcceptable" value={st.rolloverRate} tone="error" />
        <PerformanceBar
          label="Rate Competitiveness"
          kpi="priceCompetitiveness"
          value={st.rateCompetitiveness}
          tone="warning"
        />
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" sx={{ width: 130, flexShrink: 0 }}>
            Rollover Risk
          </Typography>
          <StatusChip label={st.risk} tone={RISK_TONE[st.risk]} />
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" sx={{ width: 130, flexShrink: 0 }}>
            Response Time
          </Typography>
          <Typography variant="body2" color={c.responseTime ? 'text.primary' : 'text.secondary'}>
            {c.responseTime ? `Typically ${c.responseTime.toLowerCase()}` : 'No data yet'}
          </Typography>
        </Stack>
      </Stack>
    </Panel>
  );
}

/** The routes we know this carrier on: how many shipments, how many rolled over or ran late, and how many rates it gave */
export function RoutesPanel({ st }: { st: CarrierStats }) {
  return (
    <Panel title="Routes" subtitle="Where this carrier has carried our cargo or given us rates.">
      <DataTable<RouteRow>
        rows={st.routes}
        rowKey={(r) => r.route}
        empty="No routes yet."
        columns={[
          { header: 'Route', primary: true, render: (r) => r.route },
          { header: 'Shipments', nowrap: true, render: (r) => r.shipments },
          { header: 'Rolled Over', nowrap: true, render: (r) => r.rolledOver },
          { header: 'Delayed', nowrap: true, render: (r) => r.delayed },
          { header: 'Rates Given', nowrap: true, render: (r) => r.rates },
        ]}
      />
    </Panel>
  );
}

export function OutcomesPanel({ st }: { st: CarrierStats }) {
  return (
    <Panel title="Shipment Outcomes">
      {st.total ? (
        <DonutChart
          centerLabel="Total"
          slices={[
            { label: 'Arrived', value: st.arrived, tone: 'success' },
            { label: 'Delayed', value: st.delayed, tone: 'error' },
            { label: 'Other', value: st.other, tone: 'neutral' },
          ]}
        />
      ) : (
        <EmptyState>No shipments with this carrier yet.</EmptyState>
      )}
    </Panel>
  );
}

const SERVICE_ICON = {
  FCL: <DirectionsBoatOutlined />,
  LCL: <DirectionsBoatOutlined />,
  Air: <AirplanemodeActiveOutlined />,
  Road: <LocalShippingOutlined />,
};

/** What the carrier offers, and what it can carry */
export function ServicesPanel({ c }: { c: Carrier }) {
  return (
    <Panel title="Services Offered">
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))' }}>
        <Stack spacing={1}>
          {c.services.map((s) => (
            <Stack key={s} direction="row" spacing={1} sx={{ alignItems: 'center', color: toneColor('info') }}>
              {SERVICE_ICON[s]}
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {SERVICE_NAME[s]}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <Stack spacing={0.75}>
          {c.cargoTypes.map((t) => (
            <Stack key={t} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CheckCircle fontSize="small" color="success" />
              <Typography variant="body2">{t}</Typography>
            </Stack>
          ))}
          {!c.cargoTypes.length && (
            <Typography variant="body2" color="text.secondary">
              No cargo types listed.
            </Typography>
          )}
        </Stack>
      </Box>
    </Panel>
  );
}

const unit = (r: CarrierRate) => (r.per === 'container' ? r.currency : `${r.currency}/${r.per}`);
const amount = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Every rate this carrier gave, newest first */
export function RateHistoryPanel({
  rates,
  title = 'Recent Rate History',
  limit,
}: {
  rates: CarrierRate[];
  title?: string;
  limit?: number;
}) {
  const rows = [...rates].sort((x, y) => y.date.localeCompare(x.date)).slice(0, limit ?? rates.length);
  return (
    <Panel title={title}>
      <DataTable<CarrierRate>
        rows={rows}
        rowKey={(r) => r.carrierId + r.route + r.date}
        empty="No rates recorded yet."
        columns={[
          { header: 'Date', primary: true, nowrap: true, render: (r) => shortDate(r.date) },
          { header: 'Route', render: (r) => r.route },
          {
            header: 'Service',
            nowrap: true,
            render: (r) => <Chip size="small" variant="outlined" color="info" label={r.service} />,
          },
          { header: 'Rate', nowrap: true, render: (r) => amount(r.rate) },
          { header: 'Currency', nowrap: true, render: (r) => unit(r) },
          { header: 'Validity', nowrap: true, render: (r) => `${r.validDays} days` },
          {
            header: 'Outcome',
            nowrap: true,
            action: true,
            render: (r) => <StatusChip label={r.outcome} tone={RATE_TONE[r.outcome]} />,
          },
        ]}
      />
    </Panel>
  );
}

export const RecentActivityPanel = ({ rates }: { rates: CarrierRate[] }) => (
  <ActivityList
    items={[...rates]
      .sort((x, y) => y.date.localeCompare(x.date))
      .slice(0, 5)
      .map((r) => ({
        id: r.carrierId + r.route + r.date,
        tone: RATE_TONE[r.outcome],
        date: shortDate(r.date),
        text: `Received ${r.service} rate for ${r.route}`,
      }))}
  />
);
